import { createGlobalState } from '@vueuse/core';
import { ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import reservedDatesClient from '~/services/reservedDates/reservedDatesClient';
import type { ReservedDate, StoreReservedDatePayload } from '~/services/reservedDates/reservedDatesClient.types';
import { catchRequestError } from '~/utils/request';
import type { ClientContext } from '~/services/contextClient';

export interface ReservedDateFormData {
    id?: number;
    date: string;
    marketing: boolean;
    transactional: boolean;
    reason: string;
}

export const defaultReservedDate = (): ReservedDateFormData => {
    return {
        date: '',
        marketing: true,
        transactional: false,
        reason: '',
    };
};

export const useReservedDateDetails = createGlobalState(() => {
    const toasted = useToasted();
    const context: ClientContext = { teamId: Spark.state.currentTeam.id };

    const reservedDate = ref<ReservedDateFormData>(defaultReservedDate());
    const loading = ref(false);
    const saving = ref(false);
    const error = ref('');

    let onMountedCallback = (): void => {};
    const onMountedReservedDate = (callback: () => void): void => {
        onMountedCallback = callback;
    };

    const mount = async (reservedDateId?: number): Promise<void> => {
        reservedDate.value = defaultReservedDate();
        error.value = '';

        if (reservedDateId === undefined) {
            onMountedCallback?.();
            return;
        }

        reservedDate.value.id = reservedDateId;
        loading.value = true;

        try {
            const response = await reservedDatesClient.getReservedDate(context, reservedDateId);
            const data = response.data;
            reservedDate.value = {
                id: data.id,
                date: data.date,
                marketing: data.marketing,
                transactional: data.transactional,
                reason: data.reason ?? '',
            };
            onMountedCallback?.();
        } catch (e) {
            error.value = catchRequestError(e);
            toasted?.error(error.value);
        } finally {
            loading.value = false;
        }
    };

    const save = async (): Promise<void | ReservedDate> => {
        if (saving.value) {
            return;
        }

        saving.value = true;
        error.value = '';

        try {
            const payload: StoreReservedDatePayload = {
                date: reservedDate.value.date,
                marketing: reservedDate.value.marketing,
                transactional: reservedDate.value.transactional,
                reason: reservedDate.value.reason === '' ? null : reservedDate.value.reason,
            };

            const response = reservedDate.value.id
                ? await reservedDatesClient.updateReservedDate(context, reservedDate.value.id, payload)
                : await reservedDatesClient.createReservedDate(context, payload);

            return response.data;
        } catch (e) {
            error.value = catchRequestError(e);
            throw e;
        } finally {
            saving.value = false;
        }
    };

    return {
        reservedDate,
        loading,
        saving,
        error,
        onMountedReservedDate,
        mount,
        save,
    };
});
